import { useMemo } from 'react'
import { useAuth } from '../../../../../../../auth'
import { TableState } from '../../../../../../../hooks/use-table-state'
import { UserPermissions } from '../../../../../../../types/permissions'
import { RouteMode } from '../../../../../../../types/route'
import { FormElementConfig } from '../../../../../../../forms/types'
import { toDollarCurrencyFormat } from '../../../../../../../forms/utils'
import { ApplicationDetailsFields } from './config'
import { sqmToHa } from '../../../../../../../utils/units'
import { SITE_MAINTENANCE_TABLE_COLUMNS } from '../../../../../../../forms/constants'

interface SiteMaintenanceConfigProps extends Pick<TableState, 'refreshTable' | 'deleteAction'> {
  mode?: RouteMode
  isMigrated?: boolean
  applicationId?: string
  planId?: string
}

export const useSiteMaintenanceConfig = ({
  mode,
  refreshTable,
  isMigrated,
  planId,
  applicationId,
  deleteAction,
}: SiteMaintenanceConfigProps): FormElementConfig<ApplicationDetailsFields>[] => {
  const { user } = useAuth()
  return useMemo(() => {
    if (isMigrated)
      return [
        {
          type: 'field',
          element: {
            type: 'repeating-section',
            heading: 'Site maintenance',
            sectionName: 'Site maintenance action',
            repeating: true,
            fieldId: 'maintenanceSprayingActions',
            fields: [
              {
                type: 'lookup',
                fieldId: 'type',
                lookupId: 'action_subtype',
                fieldFilters: { feature_category: 'site_maintenance' },
              },
              {
                type: 'number',
                fieldId: 'area',
              },
              {
                type: 'number',
                fieldId: 'proposedCost',
              },
            ],
          },
        },
      ]
    return [
      {
        type: 'atom',
        element: { type: 'heading', level: 2, content: 'Site maintenance' },
      },
      {
        type: 'field',
        element: {
          type: 'table',
          currentUserPermissions: user?.permissions,
          columnHeaders: [
            ...SITE_MAINTENANCE_TABLE_COLUMNS,
            {
              elementKey: 'actions',
              columnHeading: '',
              viewColumn: 'actions',
              type: 'actions-no-data',
              actions:
                mode == RouteMode.EDIT
                  ? [
                      {
                        label: 'Delete',
                        type: 'deleteAction',
                        args: [
                          {
                            elementKey: 'id',
                          },
                        ],
                        actionPermissions: [UserPermissions.EDIT_PLANS],
                      },
                    ]
                  : [],
            },
          ],
          refreshTable: refreshTable,
          tableActionHandlers: {
            deleteAction,
          },
          defaultSortBy: {
            orderColumn: 'locationName',
            orderDirection: 'desc',
          },
          requestUrl: `/api/plan/${planId}/project/${applicationId}/feature/site-maintenance`,
          rowClickAction: {
            label: 'View',
            type: 'transitionAction',
            args: [{ constant: `plans/${mode}/${planId}/plan/project/${applicationId}/action` }, { elementKey: 'id' }],
          },
          noResultsMessage: 'No site maintenance actions added',
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.siteMaintenanceArea',
          conversion: sqmToHa,
        },
      },
      {
        type: 'field',
        element: {
          type: 'read-only',
          fieldId: 'proposedTotals.siteMaintenanceCost',
          conversion: toDollarCurrencyFormat,
        },
      },
    ]
  }, [mode, refreshTable, isMigrated, planId, applicationId, deleteAction])
}
